import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faShareFromSquare } from "@fortawesome/pro-solid-svg-icons";
import moment, { MomentInput } from "moment";
import Markdown from "components/markdown/Markdown";
import { Badge } from "components/badge/Badge";
import { capitalize } from "helpers/Helpers";
import Callout from "components/callout/Callout";
import { StatusCode } from "api/protocols";
import { useGuestPortalContext } from "./GuestPortalContext";
import "./portalTickets.scss";
import "./portalPlayingPartners.scss";
import "../Customer/ClientPortal/Home/clientPortalHome.scss";
import { ButtonNew as Button } from "components/buttonNew";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import Sheet from "components/sheet/Sheet";
import { TicketStubType } from "pages/secure/facility/customer/context/contextTypes";
import { ITicket } from "redux/reducers/models/ticket";
import { QRCodeSVG } from "qrcode.react";
import classNames from "classnames";
import {
  GetCustomerTickets,
  PrintCustomerTicket,
  PostTransferCustomerTicket,
  putTransferAccept,
  GetTransfers,
  putTransferCancel,
} from "api/rpc/2024-04/customer/ticket";
import { GetCustomerFriends, ICustomerFriend, SearchCustomer } from "api/rpc/2024-04/customer/customer";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Panel from "components/panel/Panel";
import Input from "components/form/input";
import axios, { CancelToken } from "axios";
import Popup from "components/popup/Popup";
import Tabs from "components/tabs/Tabs";
import Divider from "components/divider";
import { faArrowLeft, faArrowRight, faChevronDown, faChevronUp, faTicket } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface ITicketExtended extends ITicket {
  selected: boolean;
}

interface ITicketStubExtended extends TicketStubType {
  ticket: ITicketExtended;
  badgeStatus?: "primary" | "success" | "warning" | "error" | "gray";
  recipient?: ICustomerFriend;
  created_at?: string;
}

interface ITransferredStubs {
  quantity_transferred: number;
  recipient_customer_id: number;
  status: string;
  ticket_id: number;
  id: number;
  customer_id: number;
  accepted_at: string;
  cancelled_at: string;
  title: string;
}
interface ITicketsState {
  tickets: Array<ITicketExtended>;
  ticketStubs: Array<TicketStubType>;
  transferStubs: Array<ITransferredStubs>;
}

type PortalTicketsFilters = {
  limit: number;
  start: number;
  end: number;
};

interface IStubState {
  modalOpen: boolean;
  selectedStub: ITicketStubExtended;
}

interface IPlayingPartners {
  newPlayingPartnerQuery: string;
  newPlayingPartnerEmail: string;
  newPlayingPartnerFirstName: string;
  newPlayingPartnerLastName: string;
  playerList: Array<ICustomerFriend>;
  playersToAdd: Array<ICustomerFriend>;
  filterQuery: string;
  playersLoaded: boolean;
}

export default function PortalTickets() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchedPlayers, setSearchedPlayers] = useState([]);
  const [ticketsState, setTicketsState] = useState<ITicketsState>({
    tickets: null,
    ticketStubs: [],
    transferStubs: [],
  });

  const [transferState, setTransferState] = useState({
    transferSelection: false,
    openPlayerSearch: false,
    openAddPlayer: false,
    transferWarning: false,
    selectedPlayer: null,
    selectedStubIDs: [],
    selectedTicketID: null,
    showStubList: false,
    email: "",
  });

  const [playerState, setPlayerState] = useState<IPlayingPartners>({
    newPlayingPartnerQuery: "",
    newPlayingPartnerEmail: "",
    newPlayingPartnerFirstName: "",
    newPlayingPartnerLastName: "",
    playerList: [],
    playersToAdd: [],
    filterQuery: "",
    playersLoaded: false,
  });

  const [stubState, setStubState] = useState<IStubState>({
    modalOpen: false,
    selectedStub: null,
  });

  const [pendingStubState, setPendingStubState] = useState<IStubState>({
    modalOpen: false,
    selectedStub: null,
  });

  const [acceptPendingStubState, setAcceptPendingStubState] = useState<IStubState>({
    modalOpen: false,
    selectedStub: null,
  });
  const { portalState } = useGuestPortalContext();

  const [filter, setFilter] = useState<PortalTicketsFilters>({
    limit: 5,
    start: 0,
    end: 5,
  });

  const windowSize = useWindowSize();
  const ticketStatusHeaders = [
    {
      id: "active",
      content: t("Active"),
    },
    {
      id: "archived",
      content: t("Archived"),
    },
    {
      id: "transfers",
      content: t("Transfers"),
    },
  ];

  useEffect(() => {
    void loadTickets();
    void loadUserTransfers();
  }, []);

  useEffect(() => {
    let mounted = true;
    const source = axios.CancelToken.source();
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        if (playerState.newPlayingPartnerQuery) {
          void loadSearchedPlayers(mounted, source.token);
        }
      }, 500);
    }

    return () => {
      mounted = false;
      source.cancel("Cancelled");
      clearTimeout(timeoutId);
      setSearchedPlayers([]);
    };
  }, [playerState.newPlayingPartnerQuery]);

  async function loadTickets() {
    const ticketsRes = await GetCustomerTickets(null, false);

    if (ticketsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tickets")));
      setTicketsState(prevState => ({ ...prevState, tickets: [] }));
      return;
    }

    const tickets = ticketsRes.data;

    {
      tickets?.map((ticket: any) => {
        ticket.stubs?.map((stub: any, stubIndex: number) => {
          if (stub.status === "valid") {
            const foundStub = ticket.stubs[stubIndex];
            ticket.stubs.splice(stubIndex, 1);
            ticket.stubs.unshift(foundStub);
          }
        });

        for (let i = 0; i < ticketsState.tickets?.length; i++) {
          if (ticket.id === ticketsState.tickets[i].id) {
            if (ticketsState.tickets[i].selected) {
              ticket.selected = true;
            } else {
              ticket.selected = false;
            }
          }
        }
      });
    }

    for (let i = 0; i < tickets?.length; i++) {
      tickets[i].stubsToView = tickets[i].stubs?.slice(filter.start, filter.end);
    }

    setTicketsState(prevState => ({
      ...prevState,
      tickets: tickets,
      ticketStubs: tickets.stubs,
    }));
  }

  async function loadSearchedPlayers(mounted: boolean, cancelToken: CancelToken) {
    // loading spinner while waiting for response
    if (searchedPlayers !== null) {
      setSearchedPlayers(null);
    }
    const friendsResults = await searchCustomers(cancelToken);

    //Search request cancelled via Cancel Token
    if (!friendsResults) {
      return;
    }

    const playerList = playerState.playerList;
    const friendArray = friendsResults?.filter(
      result => !playerList?.some(player => player?.id === result?.id) && result?.id !== portalState?.guest?.id,
    );

    if (friendsResults.length > 0 && friendArray.length === 0) {
      dispatch(showError(t("Player already added to playing partners")));
    }

    if (friendsResults.length === 0) {
      dispatch(showError(t("User not found")));
    }

    if (mounted) {
      setSearchedPlayers(friendArray);
    }
  }

  async function searchCustomers(cancelToken: CancelToken) {
    const searchCustomersRes = await SearchCustomer(
      { search: playerState?.newPlayingPartnerQuery },
      false,
      cancelToken,
    );

    if (searchCustomersRes.status !== StatusCode.OK) {
      if (cancelToken.reason) {
        return null;
      }

      dispatch(showError(t("Error finding player")));
      return [];
    }
    return searchCustomersRes.data as Array<ICustomerFriend>;
  }
  function handleOpenTicket(ticket: any) {
    const tickets = ticketsState.tickets;

    {
      tickets
        ?.filter((filteredTicket: any) => filteredTicket.id === ticket.id)
        .map((ticket: any) => {
          ticket.selected = !ticket.selected;
        });
    }

    setTicketsState(prevState => ({ ...prevState, tickets: tickets }));
  }

  // async function getCustomerFriends() {
  //   const friendsResults = await SearchCustomer({ search: playerState?.filterQuery }, true, undefined);

  //   if (friendsResults.status !== StatusCode.OK) {
  //     dispatch(showError(t("Error cancelling transfer")));
  //     return;
  //   }

  //   setPlayerState(prevState => ({
  //     ...prevState,
  //     playerList: friendsResults.data,
  //     playersLoaded: true,
  //   }));
  // }

  function handleNextPage(ticket: any) {
    const start = filter.start + filter.limit;
    const end = filter.end + filter.limit;

    const stubsToView = ticket.stubs.slice(start, end);
    setFilter(prev => ({ ...prev, start: start, end: end }));

    const tickets = ticketsState.tickets;
    {
      tickets
        ?.filter((filteredTicket: any) => filteredTicket.id === ticket.id)
        .map((ticket: any) => {
          ticket.stubsToView = stubsToView;
        });
    }

    setTicketsState(prevState => ({ ...prevState, tickets: tickets }));
  }

  function handlePrevPage(ticket: any) {
    const start = filter.start - filter.limit;
    const end = filter.end - filter.limit;

    const stubsToView = ticket.stubs.slice(start, end);

    setFilter(prev => ({ ...prev, start: start, end: end }));

    const tickets = ticketsState.tickets;
    {
      tickets
        ?.filter((filteredTicket: any) => filteredTicket.id === ticket.id)
        .map((ticket: any) => {
          ticket.stubsToView = stubsToView;
        });
    }

    setTicketsState(prevState => ({ ...prevState, tickets: tickets }));
  }

  async function handlePrintTicketStub() {
    const ticketRes = await PrintCustomerTicket(
      { ticket_id: stubState?.selectedStub?.ticket_id, stub_id: stubState?.selectedStub?.id },
      true,
    );
    if (ticketRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing ticket")));
      return;
    }
    window.open(String(ticketRes.data));
  }

  function handleOpenStubDetails(ticket: any, stub: any) {
    const ticketStubs = ticket.stubs;
    setStubState(prevState => ({
      ...prevState,
      modalOpen: true,
      selectedStub: { ...stub, ticket: ticket },
    }));

    setTicketsState(prevState => ({
      ...prevState,
      ticketStubs: ticketStubs,
    }));
  }

  function handleOpenPendingStub(stub?: any, ticket?: any) {
    setPendingStubState(prevState => ({
      ...prevState,
      modalOpen: !prevState.modalOpen,
      selectedStub: { ...stub, ticket: ticket },
    }));
  }

  function handleOpenAcceptPendingStub(stub?: any, ticket?: any, status?: any) {
    setAcceptPendingStubState(prevState => ({
      ...prevState,
      modalOpen: !prevState.modalOpen,
      selectedStub: { ...stub, ticket: ticket, badgeStatus: status },
    }));
  }

  async function loadUserTransfers() {
    const userTransferRes = await GetTransfers({}, true);
    if (userTransferRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading user transfers")));
      return;
    }

    setTicketsState(prev => ({ ...prev, transferStubs: userTransferRes.data }));
  }

  // when recipent_id === user_id
  async function acceptTransfer() {
    const params = {
      transfer_id: acceptPendingStubState.selectedStub.id,
    };

    const acceptPendingTicketRes = await putTransferAccept(params, true);

    if (acceptPendingTicketRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error accepting transfer")));
      return;
    }

    dispatch(showSuccess(t("Transfer successfully accepted")));
    setAcceptPendingStubState(prevState => ({
      ...prevState,
      modalOpen: !prevState.modalOpen,
      selectedStub: null,
    }));

    void loadTickets();
    void loadUserTransfers();
  }

  async function cancelTransfer() {
    const params = {
      transfer_id: pendingStubState.selectedStub.id,
    };

    const acceptPendingTicketRes = await putTransferCancel(params, true);

    if (acceptPendingTicketRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error cancelling tocket transfer")));
      return;
    }

    dispatch(showSuccess(t("Ticket transfer cancelled successfully")));
    setPendingStubState(prevState => ({
      ...prevState,
      modalOpen: !prevState.modalOpen,
      selectedStub: null,
    }));

    void loadTickets();
    void loadUserTransfers();
  }

  function handleAddStubToTransfer(stub: any) {
    let stubList = [...transferState.selectedStubIDs];
    if (stubList.includes(stub.id)) {
      stubList = stubList.filter(stubs => stubs !== stub.id);
    } else {
      stubList.push(stub.id);
    }
    setTransferState(prev => ({ ...prev, selectedStubIDs: stubList }));
  }

  async function handleShowPlayerSearchList(e: React.MouseEvent) {
    e.stopPropagation();
    if (transferState.selectedStubIDs.length < 1) {
      dispatch(showError(t("Please select at least 1 ticket stub to transfer")));
      return;
    }
    setTransferState(prevState => ({
      ...prevState,
      showStubList: !prevState.showStubList,
      openPlayerSearch: !prevState.openPlayerSearch,
    }));
    const friendsListRes = await GetCustomerFriends(null, false);

    if (friendsListRes.status !== StatusCode.OK) {
      dispatch(showError(friendsListRes.message));
      return;
    }
    setPlayerState(prevState => ({
      ...prevState,
      playerList: friendsListRes.data,
      playersLoaded: true,
    }));
  }

  function handleAddPlayer(player: any) {
    setPlayerState(prevState => ({
      ...prevState,
      playersToAdd: [...prevState.playersToAdd, player],
      newPlayingPartnerQuery: "",
    }));
    setTransferState(prev => ({ ...prev, selectedPlayer: player, email: player.email }));
    console.log("Added player", player);
  }

  async function confirmTransfer() {
    const transferRes = await PostTransferCustomerTicket(
      {
        ticket_id: transferState.selectedTicketID,
        stub_ids: transferState.selectedStubIDs,
        customer_email: transferState?.selectedPlayer?.email ?? playerState.newPlayingPartnerEmail,
        customer_first_name: playerState?.newPlayingPartnerFirstName,
        customer_last_name: playerState?.newPlayingPartnerLastName,
      },
      true,
    );
    console.log("Emailing to: ", transferState.selectedPlayer?.email);

    if (transferRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred while transferring ticket stubs")));
      return;
    }
    dispatch(showSuccess(t("Ticket stub successfully transferred")));

    setTransferState({
      transferSelection: false,
      openPlayerSearch: false,
      openAddPlayer: false,
      transferWarning: false,
      selectedPlayer: null,
      selectedStubIDs: [],
      selectedTicketID: null,
      showStubList: false,
      email: "",
    });
    setPlayerState(prevState => ({
      ...prevState,
      playersToAdd: [],
      newPlayingPartnerQuery: "",
    }));
    void loadTickets();
    void loadUserTransfers();
  }

  function handleRemoveSelectedPlayer(removePlayer: ICustomerFriend) {
    const playerList = playerState.playersToAdd;

    const updatePlayerList = playerList.filter(player => player?.id !== removePlayer.id);

    setTransferState(prevState => ({ ...prevState, selectedPlayer: null }));
    setPlayerState(prevState => ({ ...prevState, playersToAdd: updatePlayerList }));
  }

  function handleShowStubList(e: React.MouseEvent, ticket: any) {
    e.stopPropagation();
    const ticketStubs = ticket.stubs;
    e.stopPropagation();
    setTransferState(prev => ({
      ...prev,
      showStubList: !prev.showStubList,
      selectedTicketID: ticket.id,
    }));
    setTicketsState(prevState => ({
      ...prevState,
      ticketStubs: ticketStubs,
    }));
  }

  function handleAddNewPlayer() {
    if (
      !playerState.newPlayingPartnerFirstName ||
      !playerState.newPlayingPartnerLastName ||
      !playerState.newPlayingPartnerEmail
    ) {
      dispatch(showError(t("Player Information Incomplete")));
      return;
    } else {
      const newPlayer: ICustomerFriend = {
        id: 0,
        first_name: playerState.newPlayingPartnerFirstName,
        last_name: playerState.newPlayingPartnerLastName,
        email: playerState.newPlayingPartnerEmail,
        full_name: `${playerState.newPlayingPartnerFirstName} ${playerState.newPlayingPartnerLastName}`,
        initials: `${playerState.newPlayingPartnerFirstName[0]}${playerState.newPlayingPartnerLastName[0]}`,
        phone: "",
        customer_type: [{ title: t("Public") }],
      };
      setPlayerState(prevState => ({
        ...prevState,
        playersToAdd: [...prevState.playersToAdd, newPlayer],
      }));
      setTransferState(prevState => ({
        ...prevState,
        selectedPlayer: newPlayer,
      }));
      console.log("Added new player", playerState.playersToAdd);
    }
  }

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const activeTickets = ticketsState.tickets?.map(ticket => {
    if (ticket?.expiry_type === "date") {
      if (moment(ticket?.expiry_date).isAfter()) {
        return ticket;
      }
    } else {
      return ticket;
    }
  });

  const inactiveTickets = ticketsState.tickets?.map(ticket => {
    if (ticket?.expiry_type === "date") {
      if (moment(ticket?.expiry_date).isAfter()) {
        return ticket;
      }
    } else {
      return ticket;
    }
  });

  const renderTransferStubs = () => {
    const incomingTransfers = ticketsState.transferStubs.filter(
      stubs => stubs.recipient_customer_id === portalState?.guest?.id,
    );
    const outgoingTransfers = ticketsState.transferStubs.filter(
      stubs => stubs.recipient_customer_id !== portalState?.guest?.id,
    );

    return (
      <>
        {incomingTransfers?.length > 0 ? (
          <div>
            <div>
              <div className="customer-portal-transfer-header">{t("Received Tickets")}</div>
              {incomingTransfers?.map((transferredStubs: any, index: number) => {
                let badgeStatus: "primary" | "success" | "warning" | "error" | "gray";

                transferredStubs.expiry_date = moment(transferredStubs.created_at as MomentInput).add(7, "days");

                switch (transferredStubs.status.toLowerCase()) {
                  case "void":
                    badgeStatus = "error";
                    break;
                  case "redeemed":
                    badgeStatus = "gray";
                    break;
                  case "valid":
                    badgeStatus = "success";
                    break;
                  case "transfer_pending":
                    badgeStatus = "warning";
                    break;
                  case "expired":
                    badgeStatus = "gray";
                    break;
                  case "cancelled":
                    badgeStatus = "error";
                    break;
                  case "pending":
                    badgeStatus = "warning";
                    break;
                  default:
                    break;
                }

                return (
                  <div
                    className={
                      transferredStubs.status === "valid" ||
                      transferredStubs.status.toLowerCase() === "transfer_pending" ||
                      transferredStubs.status === "pending"
                        ? "customer-portal-ticket-stub-container-valid"
                        : "customer-portal-ticket-stub-container-non-valid"
                    }
                    key={index}
                    onClick={() =>
                      transferredStubs.status.toLowerCase() === "pending"
                        ? handleOpenAcceptPendingStub(transferredStubs, transferredStubs?.ticket, badgeStatus)
                        : undefined
                    }
                  >
                    <div>
                      {/* Ticket Title */}
                      <p
                        className={
                          transferredStubs.status === "valid" ||
                          transferredStubs.status.toLowerCase() === "transfer_pending" ||
                          transferredStubs.status === "pending"
                            ? "customer-portal-ticket-stub-title-valid"
                            : "customer-portal-ticket-stub-title-non-valid"
                        }
                      >
                        {/* {t("Ticket Title")} */}
                        {transferredStubs?.ticket ? transferredStubs?.ticket.title : "Ticket"}
                      </p>
                      {/* Reference # */}
                      <p
                        className={
                          transferredStubs.status === "valid" ||
                          transferredStubs.status.toLowerCase() === "transfer_pending" ||
                          transferredStubs.status === "pending"
                            ? "customer-portal-stub-number-text-valid customer-portal-stub-code"
                            : "customer-portal-stub-number-text-non-valid customer-portal-stub-code"
                        }
                      >
                        {transferredStubs.status !== "accepted" ? "" : transferredStubs.reference_number}
                      </p>

                      {/* Date */}
                      <p
                        className={classNames("customer-portal-stub-number-text-valid", {
                          "customer-portal-stub-number-text-non-valid":
                            transferredStubs.status === "valid" ||
                            transferredStubs.status.toLowerCase() === "transfer_pending" ||
                            transferredStubs.status === "pending",
                        })}
                      >
                        {t("Sent on")}{" "}
                        {moment(transferredStubs.created_at as MomentInput).format("LL")}
                      </p>
                      {/* Expiry */}
                      {transferredStubs.status !== "accepted" && transferredStubs.status !== "cancelled" ? (
                        <p
                          className={
                            transferredStubs.status === "valid"
                              ? "customer-portal-expires-on-text-valid"
                              : "customer-portal-expires-on-text-non-valid"
                          }
                        >
                          {transferredStubs.status === "expired"
                            ? t("Expired on") + " "
                            : t("Expires on") + " "}

                          {transferredStubs.expiry_date
                            ? moment(transferredStubs.expiry_date as MomentInput).format("MMMM Do, YYYY")
                            : ""}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <Badge
                        type={badgeStatus}
                        size="medium"
                        iconLeft={
                          transferredStubs.status === "valid" || transferredStubs.status === "void" ? (
                            <FontAwesomeIcon className="customer-ticket-badge-icon" icon={["fas", "circle"]} />
                          ) : (
                            <></>
                          )
                        }
                      >
                        {capitalize(String(transferredStubs.status))}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <Callout title={t("No Incoming Transfers Found")} content={t("You do not have any tickets that have been transferred to you")} type="info" />
          </div>
        )}
        <Divider />
        {outgoingTransfers?.length > 0 ? (
          <div>
            <div>
              <div className="customer-portal-transfer-sent-tickets">{t("Sent Tickets")}</div>
              {outgoingTransfers?.map((transferredStubs: any, index: number) => {
                let badgeStatus: "primary" | "success" | "warning" | "error" | "gray";

                transferredStubs.expiry_date = moment(transferredStubs.created_at as MomentInput).add(7, "days");

                switch (transferredStubs.status.toLowerCase()) {
                  case "void":
                    badgeStatus = "error";
                    break;
                  case "redeemed":
                    badgeStatus = "gray";
                    break;
                  case "valid":
                    badgeStatus = "success";
                    break;
                  case "transfer_pending":
                    badgeStatus = "warning";
                    break;
                  case "expired":
                    badgeStatus = "gray";
                    break;
                  case "cancelled":
                    badgeStatus = "error";
                    break;
                  case "pending":
                    badgeStatus = "warning";
                    break;
                  default:
                    break;
                }

                return (
                  <div
                    className={
                      transferredStubs.status === "valid" ||
                      transferredStubs.status.toLowerCase() === "transfer_pending" ||
                      transferredStubs.status === "pending"
                        ? "customer-portal-ticket-stub-container-valid"
                        : "customer-portal-ticket-stub-container-non-valid"
                    }
                    key={index}
                    onClick={() =>
                      transferredStubs.status.toLowerCase() === "pending"
                        ? handleOpenPendingStub(transferredStubs)
                        : undefined
                    }
                  >
                    <div>
                      {/* Ticket Title */}
                      <p
                        className={
                          transferredStubs.status === "valid" ||
                          transferredStubs.status.toLowerCase() === "transfer_pending" ||
                          transferredStubs.status === "pending"
                            ? "customer-portal-ticket-stub-title-valid"
                            : "customer-portal-ticket-stub-title-non-valid"
                        }
                      >
                        {/* {t("Ticket Title")} */}
                        {transferredStubs.title}
                      </p>

                      {/* Reference # */}
                      <p
                        className={
                          transferredStubs.status === "valid" ||
                          transferredStubs.status.toLowerCase() === "transfer_pending" ||
                          transferredStubs.status === "pending"
                            ? "customer-portal-stub-number-text-valid customer-portal-stub-code"
                            : "customer-portal-stub-number-text-non-valid customer-portal-stub-code"
                        }
                      >
                        {transferredStubs.reference_number}
                      </p>

                      {/* Date */}
                      <p
                        className={classNames("customer-portal-stub-number-text-valid", {
                          "customer-portal-stub-number-text-non-valid":
                            transferredStubs.status === "valid" ||
                            transferredStubs.status.toLowerCase() === "transfer_pending" ||
                            transferredStubs.status === "pending",
                        })}
                      >
                        {t("Sent on")}{" "}
                        {moment(transferredStubs.created_at as MomentInput).format("LL")}
                      </p>
                      {/* Expiry */}
                      {transferredStubs.status !== "accepted" && transferredStubs.status !== "cancelled" ? (
                        <p
                          className={
                            transferredStubs.status === "valid"
                              ? "customer-portal-expires-on-text-valid"
                              : "customer-portal-expires-on-text-non-valid"
                          }
                        >
                          {transferredStubs.status === "expired"
                            ? t("Expired on") + " "
                            : t("Expires on") + " "}

                          {transferredStubs.expiry_date
                            ? moment(transferredStubs.expiry_date as MomentInput).format("MMMM Do, YYYY")
                            : ""}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <Badge
                        type={badgeStatus}
                        size="medium"
                        iconLeft={
                          transferredStubs.status === "valid" || transferredStubs.status === "void" ? (
                            <FontAwesomeIcon className="customer-ticket-badge-icon" icon={["fas", "circle"]} />
                          ) : (
                            <></>
                          )
                        }
                      >
                        {capitalize(String(transferredStubs.status))}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <Callout title={t("No Outgoing Transfers Found")} content={t("You do not have any tickets that have been transferred")} type="info" />
          </div>
        )}
      </>
    );
  };

  function checkTicketStub(stub: TicketStubType, ticketStatus: string) {
    if (ticketStatus === "valid") {
      if (stub.status === "valid") {
        return stub;
      }
    } else if (ticketStatus === "inactive") {
      if (
        stub.status === "invalid" ||
        stub.status === "void" ||
        stub.status === "expired" ||
        stub.status === "inactive"
      ) {
        return stub;
      }
    }
  }

  function getStubTitle(stub: any) {
    // Find the ticket id with the same ticket id as the stub
    const ticket = ticketsState.tickets?.find(ticket => ticket.id === stub.ticket_id);
    if (ticket) {
      return ticket.title;
    }
    return "Untitled Ticket";
  }

  const renderTicketStubs = (tickets: ITicket[], ticketStatus?: string) => {
    return (
      <>
        {tickets !== undefined && tickets.length > 0 ? (
          <div>
            <div>
              {tickets?.map((ticket: any, index: number) => {
                if (ticket === undefined) {
                  return;
                }
                let activeCount = 0;

                const filteredStubs = ticket.stubs
                  ?.filter((filteredStub: TicketStubType) => checkTicketStub(filteredStub, ticketStatus))
                  .map((ticket: TicketStubType) => {
                    activeCount = activeCount + 1;
                    return ticket;
                  });

                return (
                  <div key={index} className="customer-portal-accordian-card-container">
                    <div
                      className="customer-ticket-header-container"
                      onClick={() => handleOpenTicket(ticket)}
                      style={{ marginBottom: "0px" }}
                    >
                      <div className="customer-ticket-title-container">
                        <div>
                          {!ticket.logo_image ? (
                            <FontAwesomeIcon className="customer-ticket-logo" icon={faTicket} />
                          ) : (
                            <img className="customer-ticket-logo" src={ticket.logo_image} />
                          )}
                        </div>
                        <div>
                          <p className="customer-ticket-title">
                            {ticket.title} {t("Tickets")}
                          </p>

                          <p className="customer-ticket-subtitle">{ticket.subtitle ?? ""}</p>
                          <p className="customer-portal-ticket-info-text">
                            {" "}
                            {activeCount} / {ticket.stubs.length}
                          </p>
                        </div>
                      </div>

                      <div className="customer-portal-customer-ticket-right-content-container">
                        {selectedTab === 0 ? (
                          <div
                            onClick={e => {
                              handleShowStubList(e, ticket);
                            }}
                            className="pr-3"
                          >
                            {t("Transfer")}
                            <FontAwesomeIcon className="pl-2" icon={faShareFromSquare} />
                          </div>
                        ) : null}

                        <div>
                          <FontAwesomeIcon
                            className="chevron-icon"
                            icon={ticket.selected ? faChevronUp : faChevronDown}
                          />
                        </div>
                      </div>
                    </div>

                    {ticket.selected ? (
                      <div className="customer-portal-ticket-container">
                        <div className="ticket-description-container">
                          <Markdown markdownText={ticket.description} />
                        </div>

                        {filteredStubs.map((stub: any, i: number) => {
                          let badgeStatus: "primary" | "success" | "warning" | "error" | "gray";

                          let isDateExpired = false;

                          if (stub.expiry_date) {
                            const dateArray = stub.expiry_date.split("-");
                            const date = new Date(Number(dateArray[0]), Number(dateArray[1]), Number(dateArray[2]));
                            isDateExpired = new Date(date.toDateString()) < new Date(new Date().toDateString());
                          }

                          switch (stub.status.toLowerCase()) {
                            case "void":
                              badgeStatus = "error";
                              break;
                            case "redeemed":
                              badgeStatus = "gray";
                              break;
                            case "valid":
                              badgeStatus = "success";
                              break;
                            case "transfer_pending":
                              badgeStatus = "warning";
                              break;
                            case "expired":
                              badgeStatus = "gray";
                              break;

                            default:
                              break;
                          }
                          return (
                            <div
                              className={
                                stub.status === "valid"
                                  ? "customer-portal-ticket-stub-container-valid"
                                  : "customer-portal-ticket-stub-container-non-valid"
                              }
                              key={i}
                              onClick={() =>
                                selectedTab === 1
                                  ? null
                                  : stub.status === "valid"
                                  ? handleOpenStubDetails(ticket, stub)
                                  : stub.status.toLowerCase() === "transfer_pending"
                                  ? handleOpenPendingStub(stub, ticket)
                                  : undefined
                              }
                            >
                              <div>
                                <p
                                  className={
                                    stub.status === "valid" || stub.status.toLowerCase() === "transfer_pending"
                                      ? "customer-portal-ticket-stub-title-valid"
                                      : "customer-portal-ticket-stub-title-non-valid"
                                  }
                                >
                                  {ticket.title}
                                </p>
                                <p
                                  className={
                                    stub.status === "valid"
                                      ? "customer-portal-stub-number-text-valid customer-portal-stub-code"
                                      : "customer-portal-stub-number-text-non-valid customer-portal-stub-code"
                                  }
                                >
                                  {stub.code}
                                </p>
                                {/* <p
                                  className={classNames("customer-portal-stub-number-text-valid", {
                                    "customer-portal-stub-number-text-non-valid": stub.status !== "valid",
                                  })}
                                >
                                  {t("Pin:")} {stub.pin}
                                </p> */}

                                <p
                                  className={
                                    stub.status === "valid"
                                      ? "customer-portal-expires-on-text-valid"
                                      : "customer-portal-expires-on-text-non-valid"
                                  }
                                >
                                  {stub.redeemed_at
                                    ? "Redeemed on "
                                    : isDateExpired
                                    ? "Expired on "
                                    : stub.expiry_date
                                    ? "Expires on "
                                    : ""}

                                  {stub.redeemed_at
                                    ? moment(stub.redeemed_at as MomentInput).format("MMMM Do, YYYY")
                                    : stub.expiry_date
                                    ? moment(stub.expiry_date as MomentInput).format("MMMM Do, YYYY")
                                    : ""}
                                </p>
                              </div>

                              <div>
                                <Badge
                                  type={badgeStatus}
                                  size="medium"
                                  iconLeft={
                                    stub.status === "valid" || stub.status === "void" ? (
                                      <FontAwesomeIcon className="customer-ticket-badge-icon" icon={faCircle} />
                                    ) : (
                                      <></>
                                    )
                                  }
                                >
                                  {capitalize(String(stub.status))}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}

                        <div className="customer-portal-tickets-footer">
                          <div>
                            <Button
                              size="small"
                              type="secondary"
                              onClick={() => handlePrevPage(ticket)}
                              disabled={
                                !ticket.stubsToView ||
                                !(filter.limit <= filter.start) ||
                                !(filter.limit <= filteredStubs.length)
                              }
                            >
                              <FontAwesomeIcon icon={faArrowLeft} className="pr-2" />
                              &nbsp;
                              {t("Prev")}
                            </Button>
                          </div>
                          <div>
                            <Button
                              size="small"
                              type="secondary"
                              onClick={() => handleNextPage(ticket)}
                              disabled={!(filteredStubs?.length === filter.limit)}
                            >
                              {t("Next")}
                              &nbsp;
                              <FontAwesomeIcon icon={faArrowRight} className="pl-2" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <Callout title={t("No Tickets Found")} content={t("You do not have any tickets")} type="info" />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div>
        <h1 className="ui-customer_page-header mb-4 mt-4">{t("Tickets")}</h1>
        <Tabs tabs={ticketStatusHeaders} selected={selectedTab} onSelect={setSelectedTab}>
          {/* Valid Tickets */}
          <div>{selectedTab === 0 ? renderTicketStubs(activeTickets, "valid") : null}</div>

          {/* Inactive/Invalid/Expired Tickets */}
          <div>{selectedTab === 1 ? renderTicketStubs(inactiveTickets, "inactive") : null}</div>

          {/* Transferred Tickets */}
          <div>{selectedTab === 2 ? renderTransferStubs() : null}</div>
        </Tabs>
      </div>

      <Panel
        open={stubState?.modalOpen && windowSize.width < MOBILE_WIDTH}
        onClose={() => setStubState(prevState => ({ ...prevState, modalOpen: false, selectedStub: null }))}
        title={stubState?.selectedStub?.ticket?.title}
        animate
        primaryAction={{
          onClick: () => void handlePrintTicketStub(),

          label: t("Download"),
        }}
        secondaryAction={{
          onClick: () => setStubState(prevState => ({ ...prevState, modalOpen: false, selectedStub: null })),
          label: t("Close"),
        }}
      >
        <div className="customer-portal-ticket-stub-info">
          <p className="ticket-stub-code">{stubState?.selectedStub?.code}</p>
          <div className="expire-date-row">
            <p
              className={classNames("ticket-stub-expiry-date", {
                "hide-expiry-badge": !stubState?.selectedStub?.expiry_date,
              })}
            >
              {t("Expires on")} {moment(stubState?.selectedStub?.expiry_date).format("LL")}
            </p>
            <Badge
              type="success"
              size="medium"
              iconLeft={<FontAwesomeIcon className="customer-ticket-badge-icon" icon={faCircle} />}
            >
              {capitalize(stubState?.selectedStub?.status)}
            </Badge>
          </div>
        </div>
        <div className="customer-portal-ticket-stub-code-container">
          <QRCodeSVG value={stubState.selectedStub?.code} />

          <p className="ticket-stub-pin">
            {t("Pin:")} {stubState?.selectedStub?.pin}
          </p>
        </div>
      </Panel>

      <Popup
        title={pendingStubState?.selectedStub?.ticket?.title ?? t("Cancel Transfer")}
        open={pendingStubState?.modalOpen}
        description={t("Are you sure you want to cancel this transfer?")}
        type="warning"
        closable
        onCancel={() =>
          setPendingStubState(prevState => ({ ...prevState, modalOpen: !prevState.modalOpen, selectedStub: null }))
        }
        cancelText={t("Close")}
        okText={t("Cancel Transfer")}
        onOk={() => void cancelTransfer()}
      />

      <Sheet
        title={
          <div className="flex flex-row" style={{ alignItems: "center", gap: "10px" }}>
            <FontAwesomeIcon className="customer-portal-ticket-icon" icon={faTicket} style={{ color: "green" }} />
            {acceptPendingStubState?.selectedStub?.ticket?.title ?? t("Accept Transfer")}
          </div>
        }
        open={acceptPendingStubState?.modalOpen}
        size="small"
        closable
        onCancel={() => {
          setAcceptPendingStubState(prevState => ({ ...prevState, modalOpen: false, selectedStub: null }));
          console.log("words", acceptPendingStubState.selectedStub);
        }}
        cancelText={t("Close")}
        onOk={acceptTransfer}
        okText={t("Accept Transfer")}
      >
        <span className="flex flex-row justify-between">
          <div className="customer-portal-stub-number-text-valid customer-portal-stub-code">
            Sent on {moment(acceptPendingStubState.selectedStub?.created_at as MomentInput).format("MMMM Do YYYY")}
          </div>
          <Badge
            type={acceptPendingStubState.selectedStub?.badgeStatus}
            size="medium"
            iconLeft={
              acceptPendingStubState.selectedStub?.status === "valid" ||
              acceptPendingStubState.selectedStub?.status === "void" ? (
                <FontAwesomeIcon icon={["fas", "circle"]} />
              ) : (
                <></>
              )
            }
          >
            {capitalize(String(acceptPendingStubState.selectedStub?.status))}
          </Badge>
        </span>
        <span className="flex flex-row justify-between">
          {acceptPendingStubState.selectedStub?.recipient ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="received-from-tooltip">{acceptPendingStubState.selectedStub?.recipient?.email}</Tooltip>
              }
            >
              <div className="customer-portal-stub-number-text-valid">
                Recieved from: {acceptPendingStubState.selectedStub?.recipient.full_name}
              </div>
            </OverlayTrigger>
          ) : null}
          {acceptPendingStubState.selectedStub?.expiry_date ? (
            <div className="customer-portal-stub-number-text-valid" style={{ fontSize: "12px", alignSelf: "center" }}>
              Expires on{" "}
              {moment(acceptPendingStubState.selectedStub?.expiry_date as MomentInput).format("MMMM Do YYYY")}
            </div>
          ) : null}
        </span>
        {acceptPendingStubState.selectedStub?.ticket?.description ? (
          <>
            <br />
            <div className="customer-por">Description</div>
            <div className="customer-portal-transfer-description">
              {acceptPendingStubState.selectedStub?.ticket?.description}
            </div>
          </>
        ) : null}
      </Sheet>

      <Sheet
        title={stubState?.selectedStub?.ticket?.title}
        open={stubState?.modalOpen && windowSize.width > MOBILE_WIDTH}
        size="small"
        closable
        onCancel={() => setStubState(prevState => ({ ...prevState, modalOpen: false, selectedStub: null }))}
        cancelText={t("Close")}
        onOk={handlePrintTicketStub}
        okText={t("Download")}
      >
        <div className="customer-portal-ticket-stub-info">
          <p className="ticket-stub-code">{stubState?.selectedStub?.code}</p>
          <div className="expire-date-row">
            <p
              className={classNames("ticket-stub-expiry-date", {
                "hide-expiry-badge": !stubState?.selectedStub?.expiry_date,
              })}
            >
              {t("Expires on")} {moment(stubState?.selectedStub?.expiry_date).format("LL")}
            </p>
            <Badge
              type="success"
              size="medium"
              iconLeft={<FontAwesomeIcon className="customer-ticket-badge-icon" icon={faCircle} />}
            >
              {capitalize(stubState?.selectedStub?.status)}
            </Badge>
          </div>
        </div>
        <div className="customer-portal-ticket-stub-code-container">
          <QRCodeSVG value={stubState.selectedStub?.code} />

          <p className="ticket-stub-pin">
            {t("Pin:")} {stubState?.selectedStub?.pin}
          </p>
        </div>
      </Sheet>

      <Sheet
        title={t("Select Ticket")}
        open={transferState?.showStubList}
        size="x-small"
        height="flexible"
        closable
        onCancel={() => setTransferState(prev => ({ ...prev, selectedStubIDs: [], showStubList: !prev.showStubList }))}
        cancelText={t("Close")}
        onOk={(e: React.MouseEvent<Element, MouseEvent>) => handleShowPlayerSearchList(e)}
        okText={t("Continue")}
        okDisabled={transferState?.selectedStubIDs.length < 1 ? true : false}
      >
        {ticketsState?.ticketStubs?.map((stub: any, index: number) => {
          if (stub.status !== "valid") {
            return;
          }
          let badgeStatus: "primary" | "success" | "warning" | "error" | "gray";

          let isDateExpired = false;

          if (stub.expiry_date) {
            const dateArray = stub.expiry_date.split("-");
            const date = new Date(Number(dateArray[0]), Number(dateArray[1]), Number(dateArray[2]));
            isDateExpired = new Date(date.toDateString()) < new Date(new Date().toDateString());
          }
          console.log("stub", stub);

          switch (stub.status.toLowerCase()) {
            case "void":
              badgeStatus = "error";
              break;
            case "redeemed":
              badgeStatus = "gray";
              break;
            case "valid":
              badgeStatus = "success";

              break;

            default:
              break;
          }

          return (
            <div
              className={
                stub.status === "valid"
                  ? `customer-portal-ticket-stub-container-valid${
                      transferState?.selectedStubIDs?.includes(stub.id) ? " selected" : ""
                    }`
                  : "customer-portal-ticket-stub-container-non-valid"
              }
              onClick={() => (handleAddStubToTransfer(stub), transferState?.selectedStubIDs?.includes(stub.id))}
              key={index}
            >
              <div>
                <p
                  className={
                    stub.status === "valid"
                      ? "customer-portal-stub-number-text-valid customer-portal-stub-code"
                      : "customer-portal-stub-number-text-non-valid customer-portal-stub-code"
                  }
                >
                  {getStubTitle(stub)}
                </p>
                <p
                  className={classNames("customer-portal-stub-number-text-valid", {
                    "customer-portal-stub-number-text-non-valid": stub.status !== "valid",
                  })}
                >
                  {stub.code}
                </p>

                <p
                  className={
                    stub.status === "valid"
                      ? "customer-portal-expires-on-text-valid"
                      : "customer-portal-expires-on-text-non-valid"
                  }
                >
                  {stub.redeemed_at
                    ? t("Redeemed on") + " "
                    : isDateExpired
                    ? t("Expired on") + " "
                    : stub.expiry_date
                    ? t("Expires on") + " "
                    : ""}

                  {stub.redeemed_at
                    ? moment(stub.redeemed_at as MomentInput).format("MMMM Do, YYYY")
                    : stub.expiry_date
                    ? moment(stub.expiry_date as MomentInput).format("MMMM Do, YYYY")
                    : ""}
                </p>
              </div>

              <div className="flex flex-row space-x-4">
                <Badge
                  type={badgeStatus}
                  size="medium"
                  iconLeft={
                    stub.status === "valid" || stub.status === "void" ? (
                      <FontAwesomeIcon className="customer-ticket-badge-icon" icon={faCircle} />
                    ) : (
                      <></>
                    )
                  }
                >
                  {capitalize(String(stub.status))}
                </Badge>

                {/* {stub.status === "valid" ? (
                    <Checkbox
                      id="stub-select"
                      size="medium"
                      readOnly
                      checked={transferState?.selectedStubIDs?.includes(stub.id)}
                    />
                  ) : null} */}
              </div>
            </div>
          );
        })}
      </Sheet>
      {console.log("transfers state", transferState)}
      <Sheet
        title={
          <img
            src="../../../public/images/users-right.svg"
            alt="users-right"
            className="customer-portal-ticket-transfer-icon"
          />
        }
        open={transferState?.openPlayerSearch}
        size="x-small"
        height="flexible"
        closable
        onCancel={() =>
          setTransferState(prevState => ({
            ...prevState,
            showStubList: !prevState.showStubList,
            openPlayerSearch: !prevState.openPlayerSearch,
          }))
        }
        cancelText={t("Go Back")}
        onOk={() =>
          setTransferState(prevState => ({
            ...prevState,
            transferWarning: !prevState.transferWarning,
            openPlayerSearch: !prevState.openPlayerSearch,
          }))
        }
        okDisabled={transferState?.selectedPlayer === null ? true : false}
        okText={t("Transfer")}
      >
        <div className="customer-portal-ticket-stub-header-text">Transfer Ticket</div>

        <div className="playing-partners-add-player-text-container">
          <p className="custoner-portal-ticket-stub-subheader-text">Select a recipent to transfer your ticket</p>
        </div>

        <div style={{ width: "100%", paddingBottom: "10px" }}>
          <Button
            type="secondary"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={() => {
              setTransferState(prevState => ({
                ...prevState,
                openAddPlayer: !prevState.openAddPlayer,
                openPlayerSearch: !prevState.openPlayerSearch,
              }));
            }}
          >
            {" "}
            Transfer by email
          </Button>
        </div>

        {playerState.playersToAdd.length > 0 ? (
          <div>
            {" "}
            Recipient(s):{" "}
            {playerState.playersToAdd.map((player: any, index: number) => {
              return (
                <div key={index} className="customer-portal-player-container">
                  <div className="flex flex-row space-x-4">
                    <div className="customer-portal-ticket-profile-picture">
                      <div>
                        <span>{player?.first_name?.charAt(0)?.toUpperCase()}</span>
                        <span>{player?.last_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                    </div>
                    <div>
                      <p className="playing-partners-add-player-name"> {player.full_name}</p>
                      <p className="playing-partners-add-player-email"> {player.email}</p>
                    </div>
                  </div>

                  <div className="align-self-center">
                    <button
                      className="customer-portal-ticket-remove-player-button"
                      onClick={() => handleRemoveSelectedPlayer(player as ICustomerFriend)}
                    >
                      {t("Remove")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Player Added */}
        {playerState.playerList.length > 0 ? (
          <>
            <div>Players</div>
            <div className="playing-partners-players-to-add-container">
              {/* <p className="playing-partners-add-list-subtext">{t("Your ticket stubs will be transferred to the following player")}</p> */}
              <div>
                {playerState.playerList?.map((player: any, index: number) => {
                  return (
                    <div
                      key={index}
                      style={{ margin: "10px 0px" }}
                      className="customer-portal-player-container"
                      onClick={() => handleAddPlayer(player)}
                    >
                      <div key={index} className="flex justify-between">
                        <div className="flex">
                          <div className="customer-portal-ticket-profile-picture">
                            <div>
                              <span>{player?.first_name?.charAt(0)?.toUpperCase()}</span>
                              <span>{player?.last_name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                          </div>
                          <div className="pr-8 pl-3">
                            <p className="playing-partners-add-player-name"> {player.full_name}</p>
                            <p className="playing-partners-add-player-email">@{player?.first_name?.toLowerCase()}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}

        {/* <Input
              labelHidden
              placeholder={t("Search by email or phone number...")}
              type="search"
              onChange={e =>
                setPlayerState(prevState => ({ ...prevState, newPlayingPartnerQuery: e.target?.value }))
              }
              value={playerState.newPlayingPartnerQuery}
              id="addPlayerSearchInput"
            /> */}
        {/* Player Search and Add Displayer */}
        {/* {searchedPlayers && searchedPlayers?.length > 0 ? (
          <div className="playing-partners-searched-players-container">
            <h1>{t("Players")}</h1>

            <div>
              {searchedPlayers?.map((player: any, index: number) => {
                return (
                  <div key={index} style={{ margin: "10px 0px" }}>
                    <div className="flex" onClick={() => handleAddPlayer(player)}>
                      <div className="playing-partners-profile-image_container pr-8">
                        <div className="client-portal-profile-default-image pr-3">
                          <div className="pl-3">
                            <span>{player?.first_name?.charAt(0)?.toUpperCase()}</span>
                            <span>{player?.last_name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="playing-partners-add-player-name"> {player.full_name}</p>
                        <p className="playing-partners-add-player-email"> {player.email}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : !searchedPlayers ? (
          <span style={{ display: "flex", justifyContent: "center", marginTop: "5px" }}>
            <Spin />
          </span>
        ) : null} */}
      </Sheet>

      <Sheet
        title={
          <img
            src="../../../public/images/users-right.svg"
            alt="users-right"
            className="customer-portal-ticket-transfer-icon"
          />
        }
        open={transferState?.openAddPlayer}
        size="x-small"
        height="flexible"
        closable
        onCancel={() =>
          setTransferState(prevState => ({
            ...prevState,
            openAddPlayer: !prevState.openAddPlayer,
            openPlayerSearch: !prevState.openPlayerSearch,
          }))
        }
        cancelText={t("Cancel")}
        onOk={() => [
          setTransferState(prevState => ({
            ...prevState,
            openPlayerSearch: !prevState.openPlayerSearch,
            openAddPlayer: !prevState.openAddPlayer,
          })),
          handleAddNewPlayer(),
        ]}
        okText={t("Add Recipient")}
      >
        <div className="gap-4 flex flex-col">
          <div className="customer-portal-ticket-stub-header-text">Add Recipient</div>
          <p className="custoner-portal-ticket-stub-subheader-text">
            Enter player details to transfer your ticket
          </p>{" "}
          <div className="customer-portal-ticket-add-player-container">
            <div className="ticket-add-recipient-text" style={{ marginBottom: "5px" }}>
              {t('First Name')}
            </div>
            <Input
              labelHidden
              placeholder={t("First Name")}
              type="text"
              onChange={e =>
                setPlayerState(prevState => ({ ...prevState, newPlayingPartnerFirstName: e.target?.value }))
              }
              value={playerState.newPlayingPartnerFirstName}
              id="addPlayerFirstNameSearchInput"
            />
          </div>
          <div className="customer-portal-ticket-add-player-container">
            <div className="ticket-add-recipient-text" style={{ marginBottom: "5px" }}>
              Last Name
            </div>
            <Input
              labelHidden
              placeholder={t("Last Name")}
              type="text"
              onChange={e =>
                setPlayerState(prevState => ({ ...prevState, newPlayingPartnerLastName: e.target?.value }))
              }
              value={playerState.newPlayingPartnerLastName}
              id="addPlayerLastNameSearchInput"
            />
          </div>
          <div className="customer-portal-ticket-add-player-container" style={{ marginBottom: "5px" }}>
            <div className="ticket-add-recipient-text" style={{ marginBottom: "5px" }}>
              Email
            </div>
            <Input
              labelHidden
              placeholder={t("Email")}
              type="text"
              onChange={e => setPlayerState(prevState => ({ ...prevState, newPlayingPartnerEmail: e.target?.value }))}
              value={playerState.newPlayingPartnerEmail}
              id="addPlayerEmailSearchInput"
            />
          </div>
        </div>
      </Sheet>

      {/* Transfer Warning Popup */}
      <Popup
        open={transferState.transferWarning}
        onCancel={() => setTransferState(prevState => ({ ...prevState, transferWarning: !prevState.transferWarning }))}
        onOk={confirmTransfer}
        type="warning"
        title={t("Transfer Ticket Stub?")}
        description={t("Are you sure you want to transfer these ticket stubs?")}
      />
    </>
  );
}
